(() => {
  if (customElements.get('lvn-review-carousel')) return;

  customElements.define('lvn-review-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slider = this.querySelector('[data-lma-review-carousel-slider]');
      this.navigation = this.querySelector('[data-review-navigation]');
      this.previous = this.querySelector('[data-review-previous]');
      this.next = this.querySelector('[data-review-next]');
      if (!this.slider || !this.navigation || !this.previous || !this.next) return;

      this.controller = new AbortController();
      const signal = this.controller.signal;
      const listen = (target, type, handler, options = {}) => {
        target.addEventListener(type, handler, { ...options, signal });
      };
      const scheduleUpdate = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };

      listen(this.previous, 'click', () => this.move(-1));
      listen(this.next, 'click', () => this.move(1));
      listen(this.slider, 'scroll', scheduleUpdate, { passive: true });
      listen(this.slider, 'keydown', (event) => {
        if (event.target !== this.slider) return;
        const rtl = getComputedStyle(this.slider).direction === 'rtl';
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.slider.scrollTo({
            left: event.key === 'Home' ? 0 : (this.slider.scrollWidth - this.slider.clientWidth) * (rtl ? -1 : 1),
            behavior: this.scrollBehavior()
          });
        }
      });

      // Touch and trackpad scrolling stay native; only add click-and-drag for a mouse.
      listen(this.slider, 'pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0 || this.slider.scrollWidth <= this.slider.clientWidth + 1) return;
        if (event.target.closest('button, input, select, textarea, [contenteditable="true"]')) return;
        this.suppressClick = false;
        clearTimeout(this.clickTimer);
        this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.slider.scrollLeft, active: false };
      });
      listen(window, 'pointermove', (event) => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.active) {
          if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) {
            this.drag = null;
            return;
          }
          if (Math.abs(dx) < 8) return;
          drag.active = true;
          this.slider.classList.add('is-dragging');
          this.slider.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
        this.slider.scrollLeft = drag.left - dx;
      }, { passive: false });
      const finish = (event) => {
        const drag = this.drag;
        if (!drag || event.pointerId !== drag.id) return;
        this.drag = null;
        if (drag.active) {
          this.suppressClick = event.type === 'pointerup';
          this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 500);
        }
        if (this.slider.hasPointerCapture(event.pointerId)) this.slider.releasePointerCapture(event.pointerId);
        this.slider.classList.remove('is-dragging');
        scheduleUpdate();
      };
      listen(window, 'pointerup', finish);
      listen(window, 'pointercancel', finish);
      listen(this.slider, 'dragstart', (event) => event.preventDefault());
      listen(this.slider, 'click', (event) => {
        if (!this.suppressClick || event.detail === 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      }, { capture: true });

      this.observer = new ResizeObserver(scheduleUpdate);
      this.observer.observe(this.slider);
      const track = this.slider.querySelector('.lma-review-carousel__track');
      if (track) this.observer.observe(track);
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.clickTimer);
      this.frame = 0;
      this.drag = null;
      this.suppressClick = false;
      this.slider?.classList.remove('is-dragging');
    }

    scrollBehavior() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    move(direction) {
      const cards = this.slider.querySelectorAll('.lma-review-carousel__card');
      const step = cards.length > 1
        ? Math.abs(cards[1].offsetLeft - cards[0].offsetLeft)
        : this.slider.clientWidth;
      const rtl = getComputedStyle(this.slider).direction === 'rtl';
      this.slider.scrollBy({ left: direction * step * (rtl ? -1 : 1), behavior: this.scrollBehavior() });
    }

    update() {
      const maximum = this.slider.scrollWidth - this.slider.clientWidth;
      const position = Math.abs(this.slider.scrollLeft);
      this.navigation.hidden = maximum <= 1;
      this.slider.classList.toggle('is-scrollable', maximum > 1);
      this.previous.disabled = position <= 1;
      this.next.disabled = position >= maximum - 1;
    }
  });
})();
